import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { catchError, throwError } from 'rxjs';
import { TokenService } from './services/token.service';
import { CityService } from './services/city.service';

// External hosts the driver app is allowed to contact via HttpClient.
// Leaflet tile servers are excluded — Leaflet fetches tiles via <img>, not HttpClient.
const ALLOWED_EXTERNAL_HOSTS = [
    'syncc.me',
    'dev.syncc.me',
];

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const tokenService = inject(TokenService);
    const cityService = inject(CityService);

    // After login, cityService.apiBaseUrl() returns the driver's silo URL.
    // Before login (no city set yet), it falls back to environment.apiUrl.
    const base = cityService.apiBaseUrl();
    const buildBase = environment.apiUrl;

    // A request is an API call if it is relative OR targets our build-time backend domain.
    // Direct-to-silo auth calls (login.page.ts firing to all silos) use absolute URLs
    // that are NOT buildBase — they bypass rewriting and go straight through.
    const isLocalAsset = req.url.startsWith('assets') || req.url.startsWith('./assets');
    const isHttp = req.url.startsWith('http');
    const isBuildBase = isHttp && req.url.startsWith(buildBase);
    const isApiUrl = !isLocalAsset && (!isHttp || isBuildBase);

    // Block external calls to hosts not on the allowlist
    if (isHttp && !isBuildBase && !isLocalAsset) {
        const host = new URL(req.url).hostname;
        if (!ALLOWED_EXTERNAL_HOSTS.some(h => host === h || host.endsWith('.' + h))) {
            console.error(`[Interceptor] Blocked external request to disallowed host: ${host}`);
            return throwError(() => new Error(`Blocked: external host '${host}' is not allowed`));
        }
    }

    // Route build-base absolute URLs to the active silo (post-login this is the driver's city).
    // Relative URLs get the active silo prepended.
    // Direct silo URLs (used during multi-silo login) are not rewritten.
    let url = req.url;
    if (isApiUrl && isBuildBase) {
        url = base + req.url.slice(buildBase.length);
    } else if (isApiUrl) {
        url = `${base}${req.url.startsWith('/') ? '' : '/'}${req.url}`;
    }

    const token = tokenService.getToken();

    const isAuthRoute = req.url.includes('/auth/login') || req.url.includes('/auth/request-otp') || req.url.includes('/auth/verify-otp') || req.url.includes('/analytics/events') || req.url.includes('/auth/account') || req.url.includes('/auth/request-account-deletion');

    if (isApiUrl && !isAuthRoute && !token) {
        router.navigate(['/login']);
        return throwError(() => new Error('No authentication token. Redirecting to login.'));
    }

    // Only add headers to our own API calls — never to external URLs (e.g. cities.json on the landing server)
    let headers = req.headers;
    if (isApiUrl) {
        headers = headers.set('ngrok-skip-browser-warning', 'true');
        if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    }

    const authReq = req.clone({ url, headers });

    if (!environment.production) {
        console.log(`[Interceptor] Token found: ${!!token} | ${authReq.method} ${authReq.url}`);
    }

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            console.error(`[API ERROR] ${error.status} on ${authReq.url}`, error.error);
            if (error.status === 401 && !isAuthRoute) {
                tokenService.clearToken();
                router.navigate(['/login']);
            }
            return throwError(() => error);
        })
    );
};
